// Tabs.tsx
import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import styled, {css} from "styled-components";

export type RadioSliderItem = {
  id: string;
  label: string;
  badge?: number | string;
  disabled?: boolean;
  icon?: {
    node: React.ReactNode;
    onClick: (id: string) => void;
    isAlwaysVisible?: boolean;
  };
};

type TabsProps = {
  items: RadioSliderItem[];
  value?: string;                 // управляемый режим
  defaultValue?: string;          // неуправляемый режим
  onChange?: (id: string) => void;
  className?: string;
  ariaLabel?: string;
};

export const RadioSlider: React.FC<TabsProps> = ({
  items,
  value,
  defaultValue,
  onChange,
  className,
  ariaLabel = "Tabs",
}) => {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string>(
    defaultValue ?? items[0]?.id
  );
  const current = isControlled ? (value as string) : internal;

  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [glider, setGlider] = useState<{left: number; width: number}>({
    left: 0,
    width: 0,
  });

  const setRef = useCallback((id: string) => (el: HTMLButtonElement | null) => {
    tabRefs.current[id] = el;
  }, []);

  const currentIndex = useMemo(
    () => items.findIndex((t) => t.id === current),
    [items, current]
  );

  const updateGlider = useCallback(() => {
    const el = tabRefs.current[current];
    const parent = listRef.current;
    if (!el || !parent) return;
    const {offsetLeft, offsetWidth} = el;
    setGlider({left: offsetLeft, width: offsetWidth});
  }, [current]);

  useEffect(() => {
    updateGlider();
  }, [updateGlider, items.length]);

  // Следим за ресайзом/шрифтами/локальными изменениями ширины
  useEffect(() => {
    const parent = listRef.current;
    if (!parent) return;
    const ro = new ResizeObserver(updateGlider);
    ro.observe(parent);
    for (const it of items) {
      const node = tabRefs.current[it.id];
      if (node) ro.observe(node);
    }
    return () => ro.disconnect();
  }, [items, updateGlider]);

  const change = (id: string) => {
    if (!isControlled) setInternal(id);
    onChange?.(id);
  };

  const focusTab = (idx: number) => {
    const safe = Math.max(0, Math.min(items.length - 1, idx));
    const id = items[safe]?.id;
    const node = id ? tabRefs.current[id] : null;
    node?.focus();
  };

  const onKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusTab(currentIndex + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusTab(currentIndex - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusTab(items.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      // активируем фокусную вкладку
      const activeEl = document.activeElement as HTMLElement | null;
      const id = activeEl?.dataset?.tabid;
      if (id) {
        e.preventDefault();
        change(id);
      }
    }
  };

  return (
    <Root className={className}>
      <TabsBar
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
      >
        {items.map((t) => {
          const selected = t.id === current;
          const handleIconClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            t.icon?.onClick(t.id);
          };
          
          return (
            <TabBtn
              key={t.id}
              ref={setRef(t.id)}
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              $selected={selected}
              disabled={t.disabled}
              data-tabid={t.id}
              onClick={() => change(t.id)}
              type="button"
              title={t.label}
            >
              {t.icon && (selected || t.icon.isAlwaysVisible) && (
                <IconWrapper onClick={handleIconClick} aria-label={`Action for ${t.label}`}>
                  {t.icon.node}
                </IconWrapper>
              )}
              <span className="label" data-label={t.label}>{t.label}</span>
              {t.badge !== undefined && (
                <Badge aria-hidden={t.badge === 0 || t.badge === "0"}>
                  {t.badge}
                </Badge>
              )}
            </TabBtn>
          );
        })}
        <Glider style={{left: glider.left, width: glider.width}} />
      </TabsBar>
    </Root>
  );
};

/* ===================== Styles ===================== */

const Root = styled.div`
  display: inline-flex;
  max-width: 100%;
`;

const TabsBar = styled.div`
  position: relative;
  display: flex;
  gap: 0.25rem;
  padding: 0.25rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const focusRing = css`
  outline: none;
  box-shadow: ${({ theme }) => theme.shadows.focus};
`;

const TabBtn = styled.button<{ $selected?: boolean }>`
  position: relative;
  z-index: 2; /* выше глайдера */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;

  height: 2rem;
  min-width: 3rem;
  padding: 0 0.75rem;
  border: 0;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: transparent;
  cursor: pointer;

  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  white-space: nowrap;
  color: ${({ theme, $selected }) => ($selected ? theme.colors.ink : theme.colors.ink3)};
  transition: color ${({ theme }) => theme.motion.fast} ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    ${focusRing}
  }

  /* Резервируем ширину жирного (выбранного) лейбла заранее — иначе при переключении бар меняет размер. */
  .label {
    display: inline-flex;
    flex-direction: column;
    text-align: center;
  }

  .label::after {
    content: attr(data-label);
    height: 0;
    overflow: hidden;
    visibility: hidden;
    font-weight: 600;
  }
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.sm};
  transition: background ${({ theme }) => theme.motion.fast} ease;
  color: inherit;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.line};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    display: block;
    width: 0.875rem;
    height: 0.875rem;
  }
`;

/** Счётчик идёт в потоке после лейбла — абсолютное позиционирование наезжало на текст. */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.125rem;
  padding: 0 0.375rem;
  flex-shrink: 0;

  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  line-height: 1;
  border-radius: ${({ theme }) => theme.radii.pill};

  background: ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.ink3};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.xs};
  }
`;

const Glider = styled.span`
  position: absolute;
  z-index: 1;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.colors.accentSoft};
  transition: left 0.25s ease-out, width 0.25s ease-out;
`;

/* ===================== Optional Panels ===================== */
/* Если хочешь панели контента рядом с Tabs — оставь эти стили:

export const TabPanel = styled.div.attrs<{active?: boolean}>(p => ({
  role: "tabpanel",
  id: `panel-${p.id ?? ""}`,
  "aria-labelledby": `tab-${p.id ?? ""}`
}))<{active?: boolean}>`
  display: ${({active}) => (active ? "block" : "none")};
  padding: 12px 2px;
`;
*/
